import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Power } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { fetchSetting, saveSetting, type ServiceToggles } from "@/lib/super-admin";
import { SERVICE_LABEL, SERVICE_TYPES, type ServiceType } from "@/lib/services";

export const Route = createFileRoute("/_authenticated/super-admin/services")({
  head: () => ({
    meta: [
      { title: "Services · Landoo Super Admin" },
      {
        name: "description",
        content: "Activate or deactivate Sale, Rent, Lease and Parking platform-wide.",
      },
    ],
  }),
  component: ServicesPage,
});

const DESCRIPTIONS: Record<ServiceType, string> = {
  sale: "Properties listed for outright sale.",
  rent: "Monthly rental listings for tenants.",
  lease: "Long-term lease agreements.",
  parking: "Paid parking spaces and hourly parking places.",
};

function ServicesPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["super-admin", "service-toggles"],
    queryFn: () => fetchSetting<ServiceToggles>("service_toggles"),
  });
  const [toggles, setToggles] = useState<ServiceToggles>({
    sale: true,
    rent: true,
    lease: true,
    parking: true,
  });
  useEffect(() => {
    if (data) setToggles(data);
  }, [data]);

  const save = async (next: ServiceToggles) => {
    setToggles(next);
    try {
      await saveSetting("service_toggles", next);
      toast.success("Service settings updated");
      qc.invalidateQueries({ queryKey: ["super-admin", "service-toggles"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save");
    }
  };

  return (
    <DashboardShell title="Services" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <p className="mb-4 text-xs text-muted-foreground">
        Deactivated services stop accepting new partner activations, new listings and new bookings.
        Existing data is preserved.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {SERVICE_TYPES.map((s) => {
          const on = toggles[s];
          return (
            <div key={s} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-brown-dark">{SERVICE_LABEL[s]}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{DESCRIPTIONS[s]}</p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                    on ? "bg-rent/15 text-rent" : "bg-coral/15 text-coral"
                  }`}
                >
                  {on ? "Active" : "Inactive"}
                </span>
              </div>
              <button
                onClick={() => save({ ...toggles, [s]: !on })}
                className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold ${
                  on ? "border border-coral/40 text-coral" : "bg-rent text-white"
                }`}
              >
                <Power className="h-3.5 w-3.5" />
                {on ? "Deactivate" : "Activate"}
              </button>
            </div>
          );
        })}
      </div>
    </DashboardShell>
  );
}
